import { FC, useMemo, useState } from 'react'

import { CheckboxSelect, Text } from '@revolut-internal/ui-kit'

import { ADDITIONAL_NESTED_FILTERS_MIN_WIDTH } from 'components/Filters/consts'
import { NestedDropdownItemBase } from 'components/Filters/NestedDropdownItemBase'
import { useOptionsWithSelectedOnTop } from 'hooks/useOptionsWithSelectedOnTop'

import { useAdditionalFilters } from './AdditionalFiltersContext'
import { PreparedFilterOption } from './types'

type AdditionalFiltersDropdownProps = {
  options: PreparedFilterOption[]
  filterKey: string
  label: string
  onSelect: VoidFunction
}

export const AdditionalFilterDropdown: FC<AdditionalFiltersDropdownProps> = ({
  options,
  label,
  filterKey,
  onSelect,
}) => {
  const [searchText, setSearchText] = useState('')
  const { additionalFilters, setAdditionalFilter } = useAdditionalFilters()
  const handleFilterChange = (key: string, value: string[]) => {
    setAdditionalFilter(key, value)
    onSelect()
  }

  const filteredOptions = useMemo(() => {
    const searchTerm = searchText.toLowerCase()

    return searchText
      ? options.reduce<PreparedFilterOption[]>((acc, curr) => {
          if (curr.label.toLowerCase().includes(searchTerm)) {
            acc.push(curr)
          } else if ('children' in curr) {
            const currentOption = { ...curr }

            currentOption.children = currentOption.children.filter(child =>
              child.label.toLowerCase().includes(searchTerm),
            )
            acc.push(currentOption)
          }

          return acc
        }, [])
      : options
  }, [options, searchText])
  const subCategoryKey = useMemo(
    () => 'children' in options[0] && options[0].subCategoryKey,
    [options],
  )
  const value = useMemo(() => {
    const filterValue = additionalFilters[subCategoryKey || filterKey] ?? []

    if (!Array.isArray(filterValue)) {
      return []
    }

    const optionValues = options.flatMap(option => {
      if ('children' in option) {
        return option.children.map(child => child.value)
      }

      return option.value
    })

    return filterValue.filter(val => optionValues.includes(val)) ?? []
  }, [filterKey, options, subCategoryKey, additionalFilters])
  const sortedOptions = useOptionsWithSelectedOnTop(filteredOptions, value)

  if (!Array.isArray(value)) {
    return null
  }

  return (
    <NestedDropdownItemBase label={label} value={value}>
      {(isShown, anchorRef) => (
        <CheckboxSelect
          anchorRef={anchorRef}
          autoClose={false}
          open={isShown}
          type="confirm"
          options={sortedOptions}
          value={value}
          labelList={label}
          minWidth={ADDITIONAL_NESTED_FILTERS_MIN_WIDTH}
          searchText={searchText}
          searchable
          onSearchText={setSearchText}
          onChange={newValue => handleFilterChange(subCategoryKey || filterKey, newValue)}
          onClick={event => event.stopPropagation()}
        >
          {option => (
            <Text
              style={{
                wordBreak: 'break-all',
              }}
            >
              {option.label}
            </Text>
          )}
        </CheckboxSelect>
      )}
    </NestedDropdownItemBase>
  )
}
